import { addToUploadQueue, updateChecksumCalculation } from '@/redux/ducks/document/upload'
import { isReceiptUploadSource } from '@/redux/ducks/document/_helpers/uploadAdmission'
import lang from '@/lang'
import { dispatch } from '@/redux/ducks/lists'
import store, { getAppState } from '@/redux/store'
import { workerMessages } from '@/worker/constants'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'

const buildWorker = () => {
  const worker = new Worker(new URL('@/worker/worker.ts', import.meta.url))

  worker.onmessage = (event: MessageEvent) => {
    const { message, payload } = event.data
    switch (message) {
      case workerMessages.FILE_CALCULATE_UPLOAD_PROGRESS:
        dispatch(updateChecksumCalculation(payload))
        return
      // FLAG:useDocuAi — a checksum that failed to calculate must become terminal, or its batch
      // waits for a file that will never arrive. classifyBatchUploads already treats a
      // `calculating` entry carrying an `error` as terminally failed, so setting the error here is
      // the whole fix.
      //
      // Gated on the receipt sources: with useDocuAi OFF the legacy accounting
      // (runLegacyUploadBatch) reads only `pending` rows and never `calculating`, so surfacing a
      // calculating error there would be a flag-OFF behaviour change for no benefit. The legacy
      // case is logged instead of silently dropped so it is at least diagnosable.
      case workerMessages.FILE_CALCULATE_UPLOAD_ERROR: {
        const { uuid: failedUuid, error } = payload
        const failed = getAppState().document.upload.calculating.find((item) => item.uuid === failedUuid)
        if (!failed) {
          return
        }
        if (!isReceiptUploadSource(failed.source)) {
          console.error(`Checksum calculation failed for ${failed.fileName}:`, error)
          return
        }
        return dispatch(updateChecksumCalculation({
          uuid: failedUuid,
          error,
          translatedError: lang.documents.uploadParseError,
        }))
      }
      case workerMessages.FILE_CALCULATE_UPLOAD_DONE:
        const {
          uuid,
          checksum,
        } = payload
        const calculation = getAppState().document.upload.calculating.find((item) => item.uuid === uuid)
        if (!calculation) {
          return
        }
        const {
          scan,
          params,
          totalChunks,
          file,
          fileName: name,
          size,
          // FLAG:useDocuAi — carried so processUploads can pick the admission rule and
          // prepareUpload can mark the pending row (see the plan's "Scheduler design").
          source,
        } = calculation

        return dispatch(addToUploadQueue({ checksum, uuid, scan, params, totalChunks, file, name, size, source }))
    }
    store.dispatch(pushResult({ ...event.data, uuid: uuidv4() }))
  }

  worker.onerror = (event: ErrorEvent) => {
    store.dispatch(pushError(event))
  }

  return worker
}

interface WorkerResult {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any,
  message: string,
  uuid: string,
}

interface WorkerState {
  results: WorkerResult[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: any[],
  worker: Worker,
}

const initialState: WorkerState = {
  results: [],
  errors: [],
  worker: buildWorker(),
}

const documentUploadSlice = createSlice({
  name: 'documentUpload',
  initialState,
  reducers: {
    pushResult(state, { payload }: PayloadAction<WorkerResult>) {
      return { ...state, results: [...state.results, payload] }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pushError(state, { payload }: PayloadAction<any>) {
      return { ...state, errors: [...state.errors, payload] }
    },
    removeResult(state, { payload }: PayloadAction<string>) {
      return { ...state, results: state.results.filter(({ uuid }) => uuid !== payload) }
    },
    terminateWorker(state) {
      state.worker.terminate()
      return { ...initialState, worker: buildWorker() }
    },
  },
})

export const {
  pushResult,
  pushError,
  terminateWorker,
} = documentUploadSlice.actions

export default documentUploadSlice.reducer
